import { useTranslation } from 'react-i18next';

import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CommonTooltip from '~/components/CommonTooltip';
import CTB from '~/components/CTB';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import { productTypes } from '~/hooks/useCtbProductInfo';
import ResponsiveActionCardV2 from '~/Nova/scenes/Site/components/ResponsiveActionCardV2';
import { formatDisplayDate } from '~/utils/date/helpers';
import { formatFixedDecimal } from '~/utils/number/helpers';

import { AiCreditBalanceCardProps } from '../utils/types';

const AI_CREDITS_CTB_MODAL_WIDTH = '832px';

const AiCreditBalanceCard = ({
  credits,
  creditExpiryDate,
  hasLlmAddon,
  onRefresh,
  isRefreshing = false,
}: AiCreditBalanceCardProps) => {
  const { t } = useTranslation('aiTooling', { keyPrefix: 'creditBalance' });
  const { localizedBrandName } = useBrandInfo();
  const formattedExpiryDate = formatDisplayDate(creditExpiryDate);

  return (
    <ResponsiveActionCardV2
      title={t('title')}
      dataTestId="ai-credit-balance-card"
      actionMenu={
        hasLlmAddon && (
          <CommonTooltip title={t('refresh')} placement="top" arrow>
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={isRefreshing}
                aria-label={t('refresh')}
                data-testid="ai-credit-balance-refresh-btn"
                color="primary"
              >
                {isRefreshing ? (
                  <CircularProgress size={18} />
                ) : (
                  <SyncIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </CommonTooltip>
        )
      }
      actionButton={
        <CTB
          product={productTypes.AI_CREDIT}
          modalWidth={AI_CREDITS_CTB_MODAL_WIDTH}
          sx={{ display: 'block', width: '100%' }}
        >
          {/* @ts-expect-error */}
          <Button
            variant="contained"
            fullWidth
            data-testid="add-ai-credits-btn"
          >
            {t('addCredits')}
          </Button>
        </CTB>
      }
    >
      {hasLlmAddon ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="baseline"
          flexWrap="nowrap"
          data-testid="ai-credit-balance-row"
        >
          <Typography
            variant="h1"
            data-testid="ai-credit-balance-value"
            sx={{
              flexShrink: 0,
              ...(credits <= 0 && {
                color: 'error.main',
                fontSize: '26px !important',
                fontWeight: '600 !important',
              }),
            }}
          >
            {credits > 0 ? formatFixedDecimal(credits) : credits}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('creditsAvailable')}
            </Typography>
            {formattedExpiryDate && (
              <Box sx={{ display: 'flex', flexShrink: 0 }}>
                <CommonTooltip
                  title={t('tooltip', { expiryDate: formattedExpiryDate })}
                  placement="top"
                  arrow
                  data-testid="ai-credit-balance-tooltip"
                >
                  <IconButton
                    size="small"
                    aria-label={t('tooltip', {
                      expiryDate: formattedExpiryDate,
                    })}
                    sx={{ p: 0 }}
                  >
                    <InfoOutlined fontSize="small" color="action" />
                  </IconButton>
                </CommonTooltip>
              </Box>
            )}
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={1} alignItems="center" pb={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesomeRounded />
            <Typography
              variant="h3"
              data-testid="ai-credit-balance-empty-title"
            >
              {t('empty.title')}
            </Typography>
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="ai-credit-balance-empty-description"
          >
            {t('empty.description', {
              brand: localizedBrandName,
            })}
          </Typography>
        </Stack>
      )}
    </ResponsiveActionCardV2>
  );
};

export default AiCreditBalanceCard;
